import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useForkRef from '../private/hooks/useForkRef';
import useEnhancedEffect from '../private/hooks/useEnhancedEffect';

// The shadow is an 8px tall strip pinned across the top edge of the footer. Only the
// upward half of its blur clears the footer, and that is what reads as the shadow.
const SHADOW_HEIGHT = 8;
// Once the content has bottomed out the strip sits 8px above the footer, so shifting it
// down by twice its height parks it behind the footer and the shadow disappears.
const SHADOW_TRAVEL = SHADOW_HEIGHT * 2;
const SHADOW_THRESHOLD = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

/**
 * Contains the main actions for `Drawer`.
 *
 * Related components: [Drawer](#drawer), [Button](#button), [ButtonGroup](#buttongroup)
 *
 * Usage:
 *
 * ```jsx
 * import { DrawerFooter } from '@one-thd/sui-atomic-components';
 * ```
 */
const DrawerFooter = React.forwardRef((props, ref) => {

  const {
    children,
    disableShadow = false,
    position = 'normal',
    variant = 'sticky',
    ...other
  } = props;

  const footerRef = useRef(null);
  const handleRef = useForkRef(footerRef, ref);
  const sentinelRef = useRef(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [shadowProgress, setShadowProgress] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  // The `flex` variant sits in the content flow rather than over it, so there is
  // nothing scrolling underneath it to cast a shadow onto.
  const shadowEnabled = variant === 'sticky' && !disableShadow;

  useEnhancedEffect(() => {
    const footer = footerRef.current;

    if (!shadowEnabled || footer === null) {
      return undefined;
    }

    setFooterHeight(footer.offsetHeight);

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      setFooterHeight(entries[0].target.offsetHeight);
    });

    resizeObserver.observe(footer);

    return () => {
      resizeObserver.disconnect();
    };
  }, [shadowEnabled]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = sentinel?.parentElement;

    if (!shadowEnabled || !sentinel || !container || typeof IntersectionObserver === 'undefined') {
      setCanScroll(false);
      return undefined;
    }

    const syncCanScroll = () => {
      setCanScroll(container.scrollHeight > container.clientHeight);
    };

    syncCanScroll();

    // The sentinel trails the scrollable content. Measured against a root cropped to
    // stop just above the footer, its intersection ratio is how far the content has
    // bottomed out, which slides the shadow away by the same proportion. Growing or
    // shrinking content moves the sentinel, so scrollability is re-read on every hit.
    const observer = new IntersectionObserver(
      (entries) => {
        syncCanScroll();
        setShadowProgress(entries[0].intersectionRatio);
      },
      {
        root: container,
        threshold: SHADOW_THRESHOLD,
        rootMargin: `0px 0px ${-(footerHeight + SHADOW_HEIGHT)}px 0px`
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [footerHeight, shadowEnabled]);

  const stickyCls = 'sui-bg-primary sui-p-4 sui-relative sui-bottom-0 sui-sticky sui-z-100';

  const flexCls = classNames('sui-bg-primary sui-p-4 sui-flex sui-items-center sui-p-4 sui-grow-0 sui-shrink-0 sui-basis-auto', {
    'sui-justify-normal': position === 'normal',
    'sui-justify-start': position === 'start',
    'sui-justify-center': position === 'center',
    'sui-justify-end': position === 'end'
  });

  // `sui-hidden` is lifted by `Drawer`, so the shadow only ever shows inside a drawer —
  // the same gate the `DrawerHeader` shadow uses.
  const shadowCls = 'drawer-shadow-div sui-sticky sui-pb-2 sui-bg-primary sui-shadow-sm sui-hidden sui-z-100';

  return (
    <>
      {shadowEnabled ? (
        <>
          {/* Padding, not height, is what holds the sentinel open: the drawer is a
              flex column and a plain height would be shrunk to nothing by the
              overflowing content. The negative margin keeps it layout neutral. */}
          <div className="sui-h-4 sui-p-2 sui--mt-4" ref={sentinelRef} />
          {canScroll ? (
            <div
              className={shadowCls}
              style={{
                bottom: footerHeight - SHADOW_HEIGHT,
                transform: `translateY(${shadowProgress * SHADOW_TRAVEL}px)`
              }}
            />
          ) : null}
        </>
      ) : null}
      <div
        className={variant === 'sticky' ? stickyCls : flexCls}
        ref={handleRef}
        {...other}
      >
        {children}
      </div>
    </>
  );
});

DrawerFooter.displayName = 'DrawerFooter';

DrawerFooter.propTypes = {
  /**
   * The contents of the drawer footer.
   */
  children: PropTypes.node,
  /**
   * If true, the footer will not raise a shadow over content scrolling beneath it.
   *
   * Only applies when variant is set to `sticky`.
   * @default false
   */
  disableShadow: PropTypes.bool,
  /**
   * Sets the child items are positioned along the container's axis.
   *
   * Only applies when variant is set to `flex`.
   * @default 'normal'
   */
  position: PropTypes.oneOf(['normal', 'start', 'center', 'end']),
  /**
   * The variant to use.
   *
   * The `sticky` variant will set the container element as bottom positioned.
   * The `flex` variant will set the container as flex: 0 0 auto;
   * @default 'sticky'
   */
  variant: PropTypes.oneOf(['flex', 'sticky']),
};

export { DrawerFooter };
